// Castintech dashboard — Minimal markdown render utility (TypeScript).
// Faz 42 PR-E — Müşteri detail drawer "Kararlar" tab içeriği için.
// Bağımlılık YOK (marked / micromark import edilmez — bundle size).
//
// Desteklenen elemanlar:
//   - # / ## / ###     → <h1>/<h2>/<h3>
//   - **bold**         → <strong>
//   - *italic*, _it_   → <em>
//   - [text](url)      → <a target=_blank rel=noopener>  (URL safety: http(s)/relative)
//   - `inline code`    → <code>
//   - - / *  item      → <ul><li>  (nested DEĞİL)
//   - 1. item          → <ol><li>
//   - Boş satırla ayrılmış metin → <p>
//
// HTML escape: <, >, &, ", ' her zaman escape edilir (XSS koruma).
// Inline parse sırası önemli — code (literal) → link → bold → italic.

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}

// URL safety — sadece http/https şemaları + relative path'ler izinli.
// javascript: / data: / vbscript: gibi şemaları reddet.
function safeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '#';
  // Relative path veya hash/anchor — izinli.
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('?') || trimmed.startsWith('.')) {
    return trimmed;
  }
  // Şemalı URL — sadece http/https.
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('http://') || lower.startsWith('https://')) return trimmed;
  // Şema yok ama relative değil (örn. "example.com/foo") — relative kabul et.
  if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return '#';
}

// Inline format: code → link → bold → italic.
// Code span (literal) önce çıkarılıp placeholder ile değiştirilir, böylece
// içindeki * ve [] gibi işaretler bold/link olarak parse edilmez.
function renderInline(s: string): string {
  // Önce HTML escape — sonra markdown sentaksı geri çevrilir.
  let out = escapeHtml(s);

  // `inline code` — placeholder ile koru.
  const codes: string[] = [];
  out = out.replace(/`([^`]+)`/g, (_m, code) => {
    codes.push(code);
    return ` CODE${codes.length - 1} `;
  });

  // [text](url) — text inline format edilebilir, url safe edilir.
  out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, url) => {
    const safe = safeUrl(url);
    return `<a href="${escapeHtml(safe)}" target="_blank" rel="noopener">${text}</a>`;
  });

  // **bold** — non-greedy, en azından 1 karakter.
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // *italic* veya _italic_ — bold çıkarıldıktan sonra çalışır.
  out = out.replace(/(^|[^*])\*([^*\n]+)\*([^*]|$)/g, '$1<em>$2</em>$3');
  out = out.replace(/(^|[^_])_([^_\n]+)_([^_]|$)/g, '$1<em>$2</em>$3');

  // Code placeholder → <code>escaped</code>.
  out = out.replace(/ CODE(\d+) /g, (_m, idx) => {
    return `<code>${escapeHtml(codes[Number(idx)] ?? '')}</code>`;
  });

  return out;
}

export function renderMd(text: string): string {
  if (!text) return '';
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Boş satır — paragraf separator.
    if (!trimmed) { i++; continue; }

    // Heading — # / ## / ###
    const h = /^(#{1,3})\s+(.+)$/.exec(trimmed);
    if (h) {
      const level = h[1].length;
      out.push(`<h${level}>${renderInline(h[2])}</h${level}>`);
      i++;
      continue;
    }

    // Unordered list — - item / * item
    if (/^[-*]\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(`<li>${renderInline(lines[i].trim().replace(/^[-*]\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join('')}</ul>`);
      continue;
    }

    // Ordered list — 1. item / 2. item
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(`<li>${renderInline(lines[i].trim().replace(/^\d+\.\s+/, ''))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join('')}</ol>`);
      continue;
    }

    // Paragraph — boş satır veya block-level eleman gelene dek topla.
    const paraLines: string[] = [trimmed];
    i++;
    while (i < lines.length) {
      const nx = lines[i].trim();
      if (!nx) break;
      if (/^#{1,3}\s+/.test(nx)) break;
      if (/^[-*]\s+/.test(nx)) break;
      if (/^\d+\.\s+/.test(nx)) break;
      paraLines.push(nx);
      i++;
    }
    out.push(`<p>${renderInline(paraLines.join(' '))}</p>`);
  }

  return out.join('\n');
}
